import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL } from "../../config";
import "./css/SalesVisitsReport.css";

const RECENT_SALES_VISIT_APIS = [
    `${API_URL}/api/get-sales-customer-visit-reports`,
    `${API_URL}/api/get-recent-sales-visits`,
    `${API_URL}/get-recent-sales-visits`,
];

const RANGE_OPTIONS = [
    { value: 7, label: "Last 7 Days" },
    { value: 30, label: "Last 30 Days" },
    { value: 90, label: "Last 90 Days" },
];

function SalesVisitsReport({ authInfo }) {
    const [recentVisits, setRecentVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedRange, setSelectedRange] = useState(7);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [isRangeOpen, setIsRangeOpen] = useState(false);
    const rangeWrapRef = useRef(null);

    useEffect(() => {
        const headers = authInfo?.token
            ? {
                  "auth-token": authInfo.token,
                  Authorization: `Bearer ${authInfo.token}`,
              }
            : {};

        const run = async () => {
            setLoading(true);
            setError("");
            try {
                const response = await fetchRecentSalesVisitsData(headers, 300);
                setRecentVisits(extractRecentVisits(response.data));
            } catch (err) {
                setRecentVisits([]);
                setError(err?.message || "Failed to load recent sales visits.");
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [authInfo?.token]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (rangeWrapRef.current && !rangeWrapRef.current.contains(event.target)) {
                setIsRangeOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    const filteredVisits = useMemo(() => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        const cutoff = new Date(today);
        cutoff.setDate(cutoff.getDate() - (Number(selectedRange) - 1));
        cutoff.setHours(0, 0, 0, 0);

        return [...recentVisits]
            .filter((row) => {
                const dt = parseVisitDate(row);
                return dt && dt >= cutoff && dt <= today;
            })
            .sort((a, b) => {
                const aTime = parseVisitDate(a)?.getTime() || 0;
                const bTime = parseVisitDate(b)?.getTime() || 0;
                return bTime - aTime;
            });
    }, [recentVisits, selectedRange]);

    const selectedRangeLabel = RANGE_OPTIONS.find((item) => item.value === selectedRange)?.label;

    return (
        <div className="salesVisitsPage">
            <div className="svPageHeader">
                <div>
                    <h1>Sales Visits</h1>
                    <p>Welcome back! Here&apos;s what&apos;s happening today.</p>
                </div>
            </div>

            <div className="salesVisitsCard">
                <div className="salesVisitsHeader">
                    <h3>Recent Sales Visits</h3>
                    <div className="svRangeWrap" ref={rangeWrapRef}>
                        <button
                            type="button"
                            className="svRangeButton"
                            onClick={() => setIsRangeOpen((prev) => !prev)}
                        >
                            {selectedRange === 7 ? "Last 7 Day" : selectedRangeLabel || "Last 7 Day"}
                            <span className="svChevron">⌄</span>
                        </button>
                        {isRangeOpen ? (
                            <div className="svRangeMenu">
                                {RANGE_OPTIONS.map((option) => (
                                    <button
                                        type="button"
                                        key={`range-${option.value}`}
                                        className="svRangeOption"
                                        onClick={() => {
                                            setSelectedRange(option.value);
                                            setIsRangeOpen(false);
                                        }}
                                    >
                                        <span>{option.label}</span>
                                        {selectedRange === option.value ? (
                                            <span className="svCheck">✓</span>
                                        ) : null}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>

                {error ? <div className="svError">{error}</div> : null}
                {loading ? (
                    <p className="svEmpty">Loading...</p>
                ) : filteredVisits.length === 0 ? (
                    <p className="svEmpty">No visit data found.</p>
                ) : (
                    <div className="svTableWrap">
                        <table className="svTable">
                            <thead>
                                <tr>
                                    <th>Customer Name ↑↓</th>
                                    <th>Salesperson</th>
                                    <th>Date ↑↓</th>
                                    <th>Status ↑↓</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredVisits.map((row, idx) => (
                                    <tr key={`sv-row-${row.id || row.visit_id || idx}`}>
                                        <td>{row.customer_name || "-"}</td>
                                        <td>{row.sales_person_name || row.sales_person || "-"}</td>
                                        <td>{formatDateOnly(row.visit_date)}</td>
                                        <td>
                                            <StatusBadge status={row.status} />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="svDetailsBtn"
                                                onClick={() => setSelectedVisit(row)}
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selectedVisit ? (
                <div className="svModalOverlay" onClick={() => setSelectedVisit(null)}>
                    <div className="svModal" onClick={(e) => e.stopPropagation()}>
                        <div className="svModalHead">
                            <h3>Visit Details</h3>
                            <button
                                type="button"
                                className="svModalClose"
                                onClick={() => setSelectedVisit(null)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="svDetailGrid">
                            <div>
                                <p className="svLabel">Customer</p>
                                <strong>{selectedVisit.customer_name || "-"}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Salesperson</p>
                                <strong>
                                    {selectedVisit.sales_person_name ||
                                        selectedVisit.sales_person ||
                                        selectedVisit.user_full_name ||
                                        "-"}
                                </strong>
                            </div>
                            <div>
                                <p className="svLabel">Sales Person Area</p>
                                <strong>{extractSalespersonArea(selectedVisit) || "-"}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Visit Date</p>
                                <strong>{formatVisitDateTime(selectedVisit.visit_date)}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Status</p>
                                <strong>{formatStatusText(selectedVisit.status)}</strong>
                            </div>
                            <div className="svFullRow">
                                <p className="svLabel">Location</p>
                                <strong>
                                    {selectedVisit.location ||
                                        selectedVisit.visit_location ||
                                        selectedVisit.customer_area ||
                                        selectedVisit.customer_address ||
                                        "-"}
                                </strong>
                            </div>
                            <div>
                                <p className="svLabel">Customer Phone</p>
                                <strong>{selectedVisit.customer_phone || selectedVisit.customer_mobile_no || "-"}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Customer Type</p>
                                <strong>{selectedVisit.customer_type || "-"}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Last PO Reference</p>
                                <strong>{selectedVisit.last_po_reference || "-"}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Competitor</p>
                                <strong>{selectedVisit.competitor || "-"}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Product Category</p>
                                <strong>
                                    {selectedVisit.product_category_name ||
                                        selectedVisit.product_category ||
                                        selectedVisit.product_category_id ||
                                        "-"}
                                </strong>
                            </div>
                            <div>
                                <p className="svLabel">Customer Priority</p>
                                <strong>{selectedVisit.customer_priority || "-"}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Visit Agenda</p>
                                <strong>{selectedVisit.visit_agenda || "-"}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Next Follow-up Date</p>
                                <strong>{formatDateOnly(selectedVisit.next_followup_date)}</strong>
                            </div>
                            <div>
                                <p className="svLabel">Assigned Salesperson ID</p>
                                <strong>{selectedVisit.assigned_sales_person_id || "-"}</strong>
                            </div>
                        </div>

                        <div className="svSection">
                            <h4>Visit Notes</h4>
                            <div className="svNotesBox">
                                {selectedVisit.visit_notes ||
                                    selectedVisit.previous_feedback ||
                                    selectedVisit.notes ||
                                    selectedVisit.followup_note ||
                                    selectedVisit.note ||
                                    "No notes added."}
                            </div>
                        </div>

                        <div className="svSection">
                            <h4>Action Items</h4>
                            <ul className="svActionList">
                                {extractVisitActionItems(selectedVisit).map((item, idx) => (
                                    <li key={`sv-action-${idx}`}>{item}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="svSection">
                            <h4>Meeting Persons</h4>
                            <ul className="svActionList">
                                {extractMeetingPersons(selectedVisit).length > 0 ? (
                                    extractMeetingPersons(selectedVisit).map((person, idx) => {
                                        const view = formatMeetingPerson(person);
                                        return (
                                            <li key={`sv-meeting-${idx}`}>
                                                <strong>{view.name}</strong>
                                                {view.details.length > 0 ? (
                                                    <div style={{ marginTop: "4px", fontSize: "14px", color: "#4b5563" }}>
                                                        {view.details.join(" | ")}
                                                    </div>
                                                ) : null}
                                            </li>
                                        );
                                    })
                                ) : (
                                    <li>No meeting person added.</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function StatusBadge({ status }) {
    const text = formatStatusText(status);
    const className =
        text === "Completed"
            ? "svStatusCompleted"
            : text === "Rescheduled"
              ? "svStatusRescheduled"
              : "svStatusPending";

    return (
        <span className={`svStatus ${className}`}>
            <span className="svStatusDot" aria-hidden="true">
                {text === "Completed" ? "✓" : text === "Rescheduled" ? "◷" : "!"}
            </span>
            {text}
        </span>
    );
}

function parseVisitDate(row) {
    const raw = row?.visit_date || row?.next_followup_date || row?.sale_created_isodt || "";
    if (!raw) return null;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
}

function formatDateOnly(date) {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    return parsed.toLocaleDateString("en-GB");
}

function formatVisitDateTime(date) {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, "0");
    const minutes = String(parsed.getMinutes()).padStart(2, "0");
    const seconds = String(parsed.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
}

function formatStatusText(status) {
    const clean = String(status || "Pending").trim().toLowerCase();
    if (clean.includes("complete")) return "Completed";
    if (clean.includes("reschedule")) return "Rescheduled";
    return "Pending";
}

function extractVisitActionItems(visit) {
    const candidates = [
        visit?.action_items,
        visit?.action_item,
        visit?.follow_up_action,
        visit?.followup_action,
        visit?.follow_up_plan,
        visit?.next_action_plan,
        visit?.next_action,
    ];

    for (const raw of candidates) {
        if (Array.isArray(raw)) {
            const normalized = raw.map((x) => String(x || "").trim()).filter(Boolean);
            if (normalized.length > 0) return normalized;
        }

        if (typeof raw === "string" && raw.trim()) {
            const normalized = raw
                .split(/\r?\n|,|;/)
                .map((x) => x.trim())
                .filter(Boolean);
            if (normalized.length > 0) return normalized;
        }
    }

    return ["No action item added."];
}

function extractMeetingPersons(visit) {
    const raw = visit?.meeting_persons_details;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return [raw];
    if (typeof raw === "string" && raw.trim()) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
            if (parsed && typeof parsed === "object") return [parsed];
        } catch (_e) {
            return [];
        }
    }
    return [];
}

function extractSalespersonArea(row) {
    if (!row) return "";
    const rawArea =
        row.sales_person_area ||
        row.area ||
        row.salesPersonArea ||
        row.salesperson_area ||
        "";
    if (typeof rawArea === "string") {
        return rawArea.trim();
    }
    if (rawArea == null) return "";
    return String(rawArea).trim();
}

function formatMeetingPerson(person) {
    if (person == null) return { name: "-", details: [] };
    if (typeof person === "string") return { name: person, details: [] };

    const name = person.meeting_person_name || person.meeting_name || person.person_name || person.name || "-";
    const details = [];
    const usedKeys = new Set(["meeting_person_name", "meeting_name", "person_name", "name"]);
    const knownFields = [
        { label: "Designation", keys: ["meeting_designation", "designation", "meeting_person_designation"] },
        { label: "Department", keys: ["meeting_department", "department"] },
        { label: "Mobile", keys: ["meeting_mobile", "mobile", "phone", "contact_no"] },
        { label: "Email", keys: ["meeting_email", "email", "mail"] },
        { label: "Company", keys: ["meeting_company", "company", "organization"] },
        { label: "Address", keys: ["meeting_address", "address"] },
    ];

    knownFields.forEach((field) => {
        const value = pickFirstNonEmpty(person, field.keys);
        if (value != null) {
            details.push(`${field.label}: ${value}`);
            field.keys.forEach((k) => usedKeys.add(k));
        }
    });

    Object.entries(person).forEach(([key, value]) => {
        if (usedKeys.has(key)) return;
        if (value == null || typeof value === "object") return;
        const text = String(value).trim();
        if (!text) return;
        details.push(`${toTitleLabel(key)}: ${text}`);
    });

    return { name: String(name), details };
}

function pickFirstNonEmpty(obj, keys) {
    for (const key of keys) {
        const value = obj?.[key];
        if (value == null) continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return null;
}

function toTitleLabel(key) {
    return String(key || "")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

function extractRecentVisits(payload) {
    const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.message)
            ? payload.message
            : Array.isArray(payload?.result)
              ? payload.result
              : [];

    return rows.filter((row) => row && (row.visit_date || row.customer_name));
}

async function fetchRecentSalesVisitsData(headers, limit = 300) {
    let lastError = null;

    for (const url of RECENT_SALES_VISIT_APIS) {
        try {
            return await axios.get(url, { headers, params: { include_all: 1, limit } });
        } catch (errGet) {
            lastError = errGet;
        }

        try {
            return await axios.post(url, { include_all: 1, limit }, { headers });
        } catch (errPost) {
            lastError = errPost;
        }
    }

    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load recent sales visits."
    );
}

const mapStateToProps = (state) => ({
    authInfo: state.authInfoReducer,
});

export default connect(mapStateToProps)(SalesVisitsReport);
